import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FaRegTrashAlt } from 'react-icons/fa';
import { AiOutlineSearch } from 'react-icons/ai';
import { usePermissionAny } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import usePageWidth from '../shared/usePageWidth';
import CustomSelect from '../CustomSelect';
import { getCathedras, getCathedrasFilters } from '../../actions/cathedras';
import Card from './Card';
import styles from './styles.module.css';

/* Режим «Кафедры» (Справочники) — перечень и карточка записи.
   Режим только для просмотра: ни «Добавить», ни «Изменить», ни «Удалить»,
   ни «Копировать», ни «Сохранить» здесь нет и быть не должно.

   Отбор, поиск, сортировка, счётчик и постраничная выдача — на сервере
   (п. 2.4, 2.6, 2.12, 3.1): клиент ничего не досортировывает и не доскрывает. */

const LIST_PATH = '/cathedra';
const CARD_PATH = '/cathedra/:id';
const PAGE_SIZE = 15;

// «Все» подставляет сам CustomSelect пустой строкой
const ALL = '';

const CATHEDRA_ACCESS = [
  ['Справочники', 'Кафедры'],
  ['Справочники', 'Кафедры. Администратор'],
];

const initialQuery = {
  search: '',
  status: ALL,
  parentId: ALL,
  sort: 'name',
  order: 'asc',
  page: 0,
};

const COLUMNS = [
  { key: 'name', title: 'Полное название кафедры' },
  { key: 'parent', title: 'Вышестоящее подразделение' },
  { key: 'staff_count', title: <>Кол-во<br />сотрудников</> },
  { key: 'status', title: 'Статус' },
];

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
  if (pages <= 1) return [];
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
  if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
  const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

  const items = [];
  list.forEach((value, i) => {
    if (i) {
      const gap = value - list[i - 1];
      if (gap === 2) items.push(list[i - 1] + 1);
      else if (gap > 2) items.push('gap');
    }
    items.push(value);
  });
  return items;
};

const Cathedra = () => {
  usePageWidth('pageWide');

  const { readRight } = usePermissionAny(CATHEDRA_ACCESS);

  const dispatch = useDispatch();
  const history = useHistory();
  const cardMatch = useRouteMatch(CARD_PATH);
  const openedId = cardMatch ? cardMatch.params.id : null;

  const [query, setQuery] = useState(initialQuery);
  const [searchInput, setSearchInput] = useState('');
  const [result, setResult] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // значения отбора «Вышестоящее подразделение» — с сервера, одним запросом при
  // входе в режим. Перечень статусов фиксирован и на сервер за ним не ходим
  // (см. statusOptions ниже).
  const [filters, setFilters] = useState({ parents: [] });

  const tableRef = useRef(null);
  const runRef = useRef(0);
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    if (!readRight) return;
    (async () => {
      const response = await dispatch(getCathedrasFilters());
      if (!aliveRef.current || response.error) return;
      setFilters({ parents: response.data.parents || [] });
    })();
  }, [dispatch, readRight]);

  // поиск уходит на сервер через 300 мс после окончания ввода
  useEffect(() => {
    if (searchInput === query.search) return undefined;
    const timeout = setTimeout(
      () => setQuery(prev => ({ ...prev, search: searchInput, page: 0 })),
      300
    );
    return () => clearTimeout(timeout);
  }, [searchInput, query.search]);

  useEffect(() => {
    if (!readRight) return;
    const run = runRef.current + 1;
    runRef.current = run;
    setLoading(true);
    (async () => {
      const response = await dispatch(getCathedras({
        search: query.search,
        status: query.status,
        parentId: query.parentId,
        sort: query.sort,
        order: query.order,
        limit: PAGE_SIZE,
        offset: query.page * PAGE_SIZE,
      }));
      if (!aliveRef.current || runRef.current !== run) return;
      if (response.error) {
        setError(response.error);
        setResult({ items: [], count: 0 });
      } else {
        setError(null);
        setResult(response.data);
      }
      setLoading(false);
    })();
  }, [dispatch, query, readRight]);

  // Перечень статусов фиксирован: «Все» (пустое значение подставляет CustomSelect),
  // «Действующая» (0), «Закрыта» (1). Список не собирается по доступным кафедрам —
  // иначе «Закрыта» пропадает, когда в выборке нет ни одной закрытой кафедры
  // (US-07). 0/1 — значения параметра status метода GET /cathedras.
  const statusOptions = useMemo(
    () => [
      { value: '0', name: 'Действующая' },
      { value: '1', name: 'Закрыта' },
    ],
    []
  );

  const parentOptions = useMemo(
    () => filters.parents.map(item => ({ value: String(item.id), name: item.name })),
    [filters.parents]
  );

  const handleSort = (key) => setQuery(prev => ({
    ...prev,
    sort: key,
    order: prev.sort === key && prev.order === 'asc' ? 'desc' : 'asc',
    page: 0,
  }));

  const handlePage = (page) => {
    setQuery(prev => ({ ...prev, page }));
    if (tableRef.current) tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isFiltersDirty = query.status !== ALL
    || query.parentId !== ALL
    || !!searchInput
    || !!query.search;

  const clearFilters = () => {
    setSearchInput('');
    setQuery(prev => ({ ...prev, search: '', status: ALL, parentId: ALL, page: 0 }));
  };

  const pages = Math.ceil((result.count || 0) / PAGE_SIZE);

  const renderValue = value => (value == null || value === ''
    ? <span className={styles.dash}>—</span>
    : value);

  const renderHead = ({ key, title }) => {
    const active = query.sort === key;
    return (
      <th
        key={key}
        className={key === 'staff_count' ? `${styles.sortable} ${styles.colCount}` : styles.sortable}
        onClick={() => handleSort(key)}
        role="button"
      >
        {title}
        <span className={active ? styles.caretOn : styles.caret}>
          {active && query.order === 'desc' ? '▼' : '▲'}
        </span>
      </th>
    );
  };

  const renderRow = item => (
    <tr key={item.idcathedra} onClick={() => history.push(`${LIST_PATH}/${item.idcathedra}`)}>
      <td className={styles.name}>{renderValue(item.name)}</td>
      <td className={styles.soft}>{renderValue(item.parent_name)}</td>
      <td className={styles.num}>{item.staff_count}</td>
      <td>{renderValue(item.status)}</td>
    </tr>
  );

  if (!readRight) return <HaventPermission />;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Кафедры</h1>
        <div className={styles.headRight}>
          <button
            type="button"
            className={styles.resetBtn}
            onClick={clearFilters}
            disabled={!isFiltersDirty}
          >
            Сбросить фильтры
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.fStatus}>
          <CustomSelect
            title="Статус"
            width="100%"
            options={statusOptions}
            value={query.status}
            setFieldValue={(name, value) => setQuery(prev => ({ ...prev, status: value, page: 0 }))}
            valuesName="status"
          />
        </div>
        <div className={styles.fParent}>
          <CustomSelect
            title="Вышестоящее подразделение"
            width="100%"
            options={parentOptions}
            value={query.parentId}
            setFieldValue={(name, value) => setQuery(prev => ({ ...prev, parentId: value, page: 0 }))}
            valuesName="parent"
          />
        </div>

        <div className={styles.srchWrap}>
          <AiOutlineSearch className={styles.srchIcon} size={18} />
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Поиск по названию"
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap} ref={tableRef}>
            <table className={styles.table}>
              <thead>
                <tr>{COLUMNS.map(renderHead)}</tr>
              </thead>
              <tbody>
                {error && (
                  <tr>
                    <td colSpan={4}>
                      <div className={styles.error}>
                        <div className={styles.errorTitle}>{error}</div>
                        Список не получен
                      </div>
                    </td>
                  </tr>
                )}
                {!error && !result.items.length && (
                  <tr>
                    <td colSpan={4}>
                      <div className={styles.empty}>
                        <div className={styles.emptyTitle}>Ничего не найдено</div>
                        Измените условия поиска или сбросьте фильтры
                      </div>
                    </td>
                  </tr>
                )}
                {!error && result.items.map(renderRow)}
              </tbody>
            </table>
          </div>

          <div className={styles.footrow}>
            <span className={styles.total}>
              Найдено записей: <b>{result.count || 0}</b>
            </span>
            {pages > 1 && (
              <div className={styles.pager}>
                <button
                  type="button"
                  disabled={query.page === 0}
                  onClick={() => handlePage(query.page - 1)}
                  aria-label="Предыдущая страница"
                >
                  ‹
                </button>
                {pagerItems(query.page + 1, pages).map((item, index) => (item === 'gap'
                  ? <span key={`gap-${index}`} className={styles.gap}>…</span>
                  : (
                    <button
                      type="button"
                      key={item}
                      className={item === query.page + 1 ? styles.pageOn : undefined}
                      onClick={() => handlePage(item - 1)}
                    >
                      {item}
                    </button>
                  )))}
                <button
                  type="button"
                  disabled={query.page + 1 >= pages}
                  onClick={() => handlePage(query.page + 1)}
                  aria-label="Следующая страница"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Идентификатор кафедры лежит в адресе страницы: по прямой ссылке карточка
          открывается сразу, закрытие возвращает к списку в том же состоянии —
          компонент перечня при этом не размонтируется (US-57). */}
      {!!openedId && (
        <Card id={openedId} onClose={() => history.push(LIST_PATH)} />
      )}
    </div>
  );
};

export default Cathedra;
